import React, { useState } from 'react';
import Btn from '../../../../components/ui/Btn';
import Switch from '../../../../components/Switch';
import ConfirmModal from '../../../../components/ui/ConfirmModal';
import { TextInput } from '../../../../components/ui/Field';

// Áreas de atención del proyecto (script 55): Ventas, Logística… El agente elige una al pasar un chat a una persona y el chat va al
// nivel 2 de esa área con menos chats. Apagar un área la saca del reparto sin borrarla; borrarla deja a sus usuarios sin área.
export default function AreasEditor({ areas, team, onCreate, onRename, onToggle, onDelete }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // { id, name }
  const [toDelete, setToDelete] = useState(null);

  const membersOf = (id) => team.filter((m) => m.role === 'attendant' && m.area_id === id);
  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  const create = () => run(async () => { if (!name.trim()) return; await onCreate(name); setName(''); });
  const saveName = () => run(async () => { if (editing.name.trim()) await onRename(editing.id, editing.name); setEditing(null); });

  return (
    <div>
      <p className="text-sm font-semibold text-fg">Áreas</p>
      <p className="text-[13px] text-fg-dim mt-0.5 mb-3">
        Por ejemplo Ventas, Logística o Recursos humanos. Al pasar un chat a una persona, el agente elige el área según lo que necesita el
        cliente y el chat va a quien atiende esa área con menos chats. Si no elige área, va a cualquier nivel 2. Cada usuario está en una sola área.
      </p>

      {areas.length > 0 && (
        <ul className="divide-y divide-line rounded-2xl ring-1 ring-line mb-3">
          {areas.map((a) => {
            const people = membersOf(a.id);
            const id = `area-${a.id}`;
            return (
              <li key={a.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <div className="min-w-[10rem] flex-1">
                  {editing?.id === a.id ? (
                    <div className="flex items-center gap-2">
                      <TextInput aria-label="Nombre del área" value={editing.name} maxLength={60} autoFocus
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditing(null); }} />
                      <Btn size="sm" variant="primary" onClick={saveName} loading={busy}>Guardar</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancelar</Btn>
                    </div>
                  ) : (
                    <>
                      <p className={`text-sm ${a.is_active ? 'text-fg' : 'text-fg-faint line-through'}`}>{a.name}</p>
                      <p className={`text-xs ${people.length || !a.is_active ? 'text-fg-faint' : 'text-caution'}`}>
                        {people.length ? people.map((p) => p.full_name || 'Sin nombre').join(', ')
                          : 'Nadie la atiende: sus chats irán al administrador del proyecto'}
                      </p>
                    </>
                  )}
                </div>
                {editing?.id !== a.id && (
                  <div className="flex items-center gap-2">
                    <Switch id={id} size="sm" checked={a.is_active} disabled={busy} label={`Área ${a.name} activa`} onChange={(v) => run(() => onToggle(a.id, v))} />
                    <label htmlFor={id} className="text-xs text-fg-dim cursor-pointer mr-2">Activa</label>
                    <Btn size="sm" variant="ghost" onClick={() => setEditing({ id: a.id, name: a.name })}>Renombrar</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setToDelete(a)}>Borrar</Btn>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center gap-2 max-w-md">
        <TextInput aria-label="Nombre del área nueva" placeholder="Nombre del área (por ejemplo, Ventas)" value={name} maxLength={60}
          onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
        <Btn size="sm" onClick={create} disabled={!name.trim()} loading={busy}>Agregar área</Btn>
      </div>

      <ConfirmModal open={Boolean(toDelete)} onClose={() => setToDelete(null)} title={`Borrar el área ${toDelete?.name ?? ''}`}
        confirmLabel="Borrar área" tone="danger" onConfirm={() => onDelete(toDelete.id)}>
        <p>Sus usuarios se quedan sin área (siguen recibiendo chats sin área) y los chats que ya atendieron conservan su historial.</p>
        <p>Si solo quieres dejar de usarla por un tiempo, mejor apágala.</p>
      </ConfirmModal>
    </div>
  );
}
